import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle, FileText, XCircle } from "lucide-react";
import { API_BASE_URL } from "../../config/constants";
import { apiClient } from "../../utils/apiClient";

type CeremonyStatus =
  | "loading"
  | "loaded"
  | "error"
  | "expired"
  | "completed"
  | "canceled";

export const EmbeddedCeremony: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CeremonyStatus>("loading");
  const [ceremonyUrl, setCeremonyUrl] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  // Get verification token from search params (passed from EmailVerification)
  const verificationToken = searchParams.get("verificationToken");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("Participant token is missing");
      return;
    }

    if (!verificationToken) {
      setStatus("error");
      setError(
        "Verification token is missing. Please verify your email first.",
      );
      return;
    }

    requestCeremonyUrl();
  }, [token, verificationToken]);

  // Listen for postMessage events from the signing iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const { type, error_type, error_message } = event.data || {};

      switch (type) {
        case "ceremony.completed":
          console.log("Ceremony completed successfully.");
          setStatus("completed");
          break;
        case "ceremony.canceled":
          console.log("Ceremony was canceled by the user.");
          setStatus("canceled");
          break;
        case "ceremony.failed":
          console.error(`Ceremony failed: ${error_type} - ${error_message}`);
          setStatus("error");
          setError(
            error_message || "The signing ceremony failed. Please try again.",
          );
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const requestCeremonyUrl = async () => {
    try {
      const response = await apiClient.requestCeremony(
        token!,
        verificationToken!,
      );

      if (!response) {
        setStatus("error");
        setError("Failed to load signing ceremony");
        return;
      }

      setCeremonyUrl(response.ceremonyUrl);
      setExpiresAt(response.expiresAt);
      setStatus("loaded");
    } catch (err: any) {
      setStatus("error");
      setError("Network error. Please check your connection and try again.");
    }
  };

  const handleResendVerification = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/participants/resend-verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            participantToken: token,
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        navigate(
          `/request/expired?participantToken=${token}&error=Ceremony expired. Please check your email for a new verification link.`,
        );
      } else {
        setError(data.message || "Failed to resend verification email");
      }
    } catch (err: any) {
      setError("Failed to resend verification email. Please try again.");
    }
  };

  const handleCeremonyError = () => {
    setStatus("expired");
    setError("The signing ceremony has expired or encountered an error.");
  };

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-solid border-brand-500 border-t-transparent"></div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">
            Loading signing ceremony...
          </h2>
          <p className="text-gray-600">Please wait a moment</p>
        </div>
      </div>
    );
  }

  if (status === "completed") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full rounded-lg bg-white p-8 shadow-lg">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="mb-2 text-center text-xl font-semibold text-gray-900">
            Signing Complete
          </h2>
          <p className="mb-6 text-center text-gray-600">
            Thank you for using Casekeep. Your document has been signed
            successfully.
          </p>
          <button
            onClick={() => navigate("/")}
            className="w-full rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (status === "canceled") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full rounded-lg bg-white p-8 shadow-lg">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
            <XCircle className="h-8 w-8 text-yellow-600" />
          </div>
          <h2 className="mb-2 text-center text-xl font-semibold text-gray-900">
            Signing Canceled
          </h2>
          <p className="mb-6 text-center text-gray-600">
            You have canceled the signing process. If this was a mistake, you
            can request a new signing link.
          </p>
          {token && (
            <button
              onClick={handleResendVerification}
              className="w-full rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600 mb-3"
            >
              Request New Signing Link
            </button>
          )}
          <button
            onClick={() => navigate("/")}
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (status === "error" || status === "expired") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md w-full rounded-lg bg-white p-8 shadow-lg">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="mb-2 text-center text-xl font-semibold text-gray-900">
            {status === "expired"
              ? "Ceremony Expired"
              : "Unable to Load Ceremony"}
          </h2>
          <p className="mb-6 text-center text-gray-600">{error}</p>

          {token && (
            <button
              onClick={handleResendVerification}
              className="w-full rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600 mb-3"
            >
              Request New Verification Link
            </button>
          )}

          <button
            onClick={() => navigate("/")}
            className="w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Header with context */}
      <div className="border-b border-gray-200 bg-white px-6 py-4 shadow-sm">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-brand-50 p-2">
              <FileText className="h-6 w-6 text-brand-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                Document Signing
              </h1>
              <p className="text-sm text-gray-600">
                Please review and sign the document below
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Ceremony iframe */}
      <div className="relative flex-1">
        {ceremonyUrl && (
          <iframe
            src={ceremonyUrl}
            className="absolute inset-0 h-full w-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            referrerPolicy="no-referrer"
            onError={handleCeremonyError}
            title="Signing Ceremony"
          />
        )}
      </div>

      {/* Expiry notice */}
      {expiresAt && (
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-3">
          <div className="mx-auto max-w-7xl">
            <p className="text-center text-sm text-gray-500">
              This signing session expires at{" "}
              {new Date(expiresAt).toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
